import {ReactElement, useRef, useContext} from "react"
import {Warehouse, } from "./api/warehouse"
import {Item} from "./api/item"
import {ItemCode} from "./api/types"
import {ProjectedStock} from "./projectedstock"
import {ScenariosTableRow} from "./ScenariosTableRow"
import {ScenariosTableRowDetail} from "./ScenariosTableRowDetail"
import {FullScreenButton} from "./FullScreenButton"
import "./ScenariosTable.scss"
import {AllDataContext} from "./LoggedinMainPage"


type Props = {
  lines: readonly string[],
  warehouses: readonly Warehouse[]
  items: readonly Item[]
}
export function ScenariosTable(
  {lines, items, warehouses}: Props
): ReactElement | null {
  const allData = useContext(AllDataContext)
  const warehouseCodes = new Set(warehouses.map(wh => wh.code))

  const thisTable = useRef<HTMLDivElement>(null)
  if (lines.filter(s => s.trim()).length === 0) {
    return null
  }

  const tableRowKeys = new Set<string>()
  const countByItemCode = new Map<ItemCode, number>()
  let allProjectedStocks: readonly ProjectedStock[] = []
  return <div ref={thisTable}>
      <FullScreenButton elementToFullScreen={thisTable} />
      <table className="ScenariosTable">
      <thead>
        <tr>
          <th>#</th>
          <th colSpan={2}>Item</th>
          <th className="dates">dates</th>
          {warehouses.map((wh, i) => <th key={wh.code}>
            {i ? "+ " : ""}{wh.description}
          </th>)}
        </tr>
      </thead>
      <tbody>
        {lines.map(line => line.trim()).map(line => {
            if (line.trim().length === 0) {
              return null
            }
            const parseLine = line.trim().match(
              /^(?<countstr>[1-9][0-9]*) (?<itemCode>.*)$/)
            const baseKey = parseLine ? parseLine.groups!.itemCode : line
            let key = baseKey
            for (let i=0; ; i++) {
              if (!tableRowKeys.has(key)) {
                break
              }
              key = `${baseKey}_${i}`
            }
            tableRowKeys.add(key)

            if (!parseLine) {
              return <tr key={key} className="problem">
                <td colSpan={3}>Problem with line <i>{line}</i></td>
                </tr>
            }
            const {countstr, itemCode} = parseLine!.groups!
            const count = parseInt(countstr, 10)
            const item = items.find(item => item.code === itemCode)
            if (!item) {
              return <tr key={key} className="problem">
                  <td className="count">{count}</td>
                  <td className="itemCode">{itemCode}</td>
                  <td className="itemDescription">Unknown item</td>
                </tr>
            }
            if (countByItemCode.has(item.code)) {
              return <tr key={key} className="problem">
                  <td className="count">{count}</td>
                  <td className="itemCode">{itemCode}</td>
                  <td className="itemDescription">
                    Each item can only be in the list once
                  </td>
                </tr>
            }
            countByItemCode.set(item.code, count)
            const projectedStocks = allData.projectedStocks.filter(
              ps => warehouseCodes.has(ps.warehouseCode) && ps.itemCode === item.code)
            allProjectedStocks = [...allProjectedStocks, ...projectedStocks]

            return <ScenariosTableRow key={key}
                        projectedStocks={projectedStocks}
                        count={count} item={item} warehouses={warehouses} />
          })
        }
        {!!countByItemCode.size &&
          <tr className="summary">
            <td className="count">
              {[...countByItemCode.values()].reduce((a, b) => a + b, 0)}
            </td>
            <td className="itemDescription" colSpan={2}>
              Whole order
            </td>
            <ScenariosTableRowDetail
              warehouses={warehouses}
              countByItemCode={countByItemCode}
              projectedStocks={allProjectedStocks}
              isSummary={true}
            />
          </tr>
        }
      </tbody>
    </table>
  </div>

}
